import { useMemo } from 'react';
import { todayKey } from './state';
import { estimateEnergyBalance } from './logic/energy';
import { fmtInt } from './stats/nutritionData';
import { Button, Card, Input, NumberInput, Toggle } from './ui';
import { BackupCard } from './body/BackupCard';
import { WeightGoalEditor } from './body/WeightGoal';
import { PushCard } from './PushCard';
import { AppearanceCard } from './AppearanceCard';
import { balanceMacros, macroKcal } from './macros';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Profile tab                                                               */
/* ────────────────────────────────────────────────────────────────────────── */

export function ProfileTab({ t, state, setState, token, onRestore, ui, onUi, isDark, onTheme, showTrading }) {
  const profile = state.profile;
  const update = (patch) => setState(prev => ({ ...prev, profile: { ...prev.profile, ...patch } }));
  // Calories and macros stay in step: protein, carbs or calories move fats,
  // fats move carbs (macros.js).
  const setTarget = (field, v) => setState(prev => ({
    ...prev, profile: { ...prev.profile, ...balanceMacros(prev.profile.calorieTarget, prev.profile.macros, field, v) },
  }));
  const fromMacros = macroKcal(profile.macros);
  const offBy = fromMacros - (profile.calorieTarget || 0);
  // The Stats estimate, as a reference while editing the target.
  const { nutrition, logStatus, bodyMetrics, supplements } = state;
  const today = todayKey();
  const maintenance = useMemo(() => {
    const est = estimateEnergyBalance({ nutrition, logStatus, profile, bodyMetrics, supplements }, today);
    return est && est.ok ? est.tdee : null;
  }, [nutrition, logStatus, profile, bodyMetrics, supplements, today]);

  const fld = (label, child) => (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textFaint, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      {child}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card t={t} padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 14 }}>Profile</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {fld('Display name',
            <Input t={t} value={profile.displayName} onChange={e => update({ displayName: e.target.value })} placeholder="Your name" />
          )}
          {fld('Goal',
            <select value={profile.goal} onChange={e => update({ goal: e.target.value })}
              style={{ width: '100%', background: t.inputBg, color: t.text, border: `1px solid ${t.inputBorder}`, borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none' }}>
              <option value="build muscle">Build muscle</option>
              <option value="lose fat">Lose fat</option>
              <option value="recomp">Body recomp</option>
              <option value="strength">Strength</option>
              <option value="maintenance">Maintenance</option>
            </select>
          )}
          {fld('Environment',
            <select value={profile.environment} onChange={e => update({ environment: e.target.value })}
              style={{ width: '100%', background: t.inputBg, color: t.text, border: `1px solid ${t.inputBorder}`, borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none' }}>
              <option value="gym">Gym</option>
              <option value="home">Home</option>
              <option value="hybrid">Hybrid</option>
            </select>
          )}
          {fld('Training days / week',
            <NumberInput t={t} value={profile.trainingDays} onChange={v => update({ trainingDays: Math.max(1, Math.min(7, v || 1)) })} step={1} min={1} />
          )}
        </div>
      </Card>

      {onUi && <AppearanceCard t={t} ui={ui} onUi={onUi} isDark={isDark} onTheme={onTheme} showTrading={showTrading} />}

      <WeightGoalEditor t={t} state={state} setState={setState} today={today} />

      <Card t={t} padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 14 }}>Nutrition targets</div>
        <div className="fit-quad" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {fld('Calories',
            <>
              <NumberInput t={t} value={profile.calorieTarget} onChange={v => setTarget('calories', v || 0)} step={50} suffix="kcal" />
              {maintenance !== null && (
                <div style={{ fontSize: 11, color: t.textFaint, marginTop: 5 }}>maintenance est. ~{fmtInt(maintenance)}</div>
              )}
            </>
          )}
          {fld('Protein',
            <NumberInput t={t} value={profile.macros.protein} onChange={v => setTarget('protein', v || 0)} step={5} suffix="g" />
          )}
          {fld('Carbs',
            <NumberInput t={t} value={profile.macros.carbs} onChange={v => setTarget('carbs', v || 0)} step={5} suffix="g" />
          )}
          {fld('Fats',
            <NumberInput t={t} value={profile.macros.fats} onChange={v => setTarget('fats', v || 0)} step={5} suffix="g" />
          )}
        </div>
        <div style={{ fontSize: 12, marginTop: 12, color: Math.abs(offBy) > 10 ? '#f59e0b' : t.textMuted }}>
          Macros add up to {fmtInt(fromMacros)} kcal
          {Math.abs(offBy) > 10 ? ` (${offBy > 0 ? '+' : ''}${fmtInt(offBy)} vs target)` : ''}
          {' · '}changing protein or carbs adjusts fats, changing fats adjusts carbs
        </div>
      </Card>

      <Card t={t} padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 14 }}>Reminders</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, color: t.text }}>Daily workout reminder</div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>Browser notification at the time you set</div>
          </div>
          <Toggle t={t} checked={state.reminders.enabled}
            onChange={v => setState(prev => ({ ...prev, reminders: { ...prev.reminders, enabled: v } }))} />
        </div>
        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'minmax(0, 150px) minmax(0, 1fr)', gap: 12, alignItems: 'center' }}>
          <Input t={t} type="time" value={profile.reminderTime}
            onChange={e => update({ reminderTime: e.target.value })} />
          <Button t={t} size="sm" variant="ghost" style={{ whiteSpace: 'normal', minWidth: 0 }} onClick={() => {
            if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
              Notification.requestPermission();
            }
          }}>Allow notifications</Button>
        </div>
      </Card>

      <PushCard t={t} token={token} />

      <BackupCard t={t} state={state} token={token} onRestore={onRestore} />
    </div>
  );
}
